<script setup lang="ts">
import { ref, useId, watch } from 'vue'

const props = defineProps<{
  defaultOpen?: boolean
}>()

const emit = defineEmits<{
  toggle: [open: boolean]
}>()

const isOpen = ref(props.defaultOpen ?? false)
const contentId = useId()

function toggle() {
  isOpen.value = !isOpen.value
}

watch(isOpen, (open) => emit('toggle', open), { immediate: true })
</script>

<template>
  <div class="accordion" :class="{ 'accordion--open': isOpen }">
    <button
      type="button"
      class="accordion__summary"
      :aria-expanded="isOpen"
      :aria-controls="contentId"
      @click="toggle"
    >
      <slot name="summary" />
      <svg
        class="accordion__chevron"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 4l4 4-4 4"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <div :id="contentId" class="accordion__content" :inert="!isOpen" :aria-hidden="!isOpen">
      <div class="accordion__inner">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.accordion {
  &__summary {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: space(3);
    padding: 0;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: inherit;
    user-select: none;

    &:focus-visible {
      outline: 2px solid color('accent');
      outline-offset: 2px;
      border-radius: $radius-md;
    }
  }

  &__chevron {
    flex-shrink: 0;
    transition: transform $duration-base $easing-standard;
  }

  &--open &__chevron {
    transform: rotate(90deg);
  }

  &__content {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows $duration-slow $easing-standard;
  }

  &--open &__content {
    grid-template-rows: 1fr;
  }

  &__inner {
    overflow: hidden;
    min-height: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    &__content {
      transition: none;
    }

    &__chevron {
      transition: none;
    }
  }
}
</style>
